if (!customElements.get('aura-table-looks')) {
  customElements.define('aura-table-looks', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.tabs = [...this.querySelectorAll('[data-look-tab]')];
      this.panels = [...this.querySelectorAll('[data-look-panel]')];
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.panels.length > 1;
      this.paused = !this.autoplayEnabled || this.reducedMotion.matches || Boolean(window.Shopify?.designMode);
      this.querySelectorAll('[data-slide-step]').forEach(button => {
        button.addEventListener('click', () => {
          const direction = Number(button.dataset.slideStep);
          this.step(direction);
          this.panels.find(panel => !panel.hidden)?.querySelector(`[data-slide-step="${direction}"]`)?.focus({ preventScroll: true });
        }, { signal });
      });
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.pause();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.addEventListener('focusin', () => this.pause(), { signal });
      this.visibility?.disconnect();
      this.visibility = new IntersectionObserver(() => this.schedule(), { threshold: [0, .25, .6, 1] });
      this.panels.forEach(panel => {
        const hero = panel.querySelector('.ae-hero');
        this.visibility.observe(hero);
        let touchStart = null;
        hero.addEventListener('pointerdown', event => {
          if (event.pointerType === 'mouse' || event.target.closest('a, button')) return;
          touchStart = { x: event.clientX, y: event.clientY };
        }, { passive: true, signal });
        hero.addEventListener('pointercancel', () => { touchStart = null; }, { signal });
        hero.addEventListener('pointerup', event => {
          if (!touchStart) return;
          const dx = event.clientX - touchStart.x;
          const dy = event.clientY - touchStart.y;
          touchStart = null;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
        }, { passive: true, signal });
        const shop = panel.querySelector('.ae-table-shop');
        if (shop) {
          this.visibility.observe(shop);
          shop.addEventListener('pointerdown', () => this.pause(), { passive: true, signal });
        }
        hero.querySelector('.ae-button')?.addEventListener('click', () => this.pause(), { signal });
      });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(tab.dataset.lookTab), { signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(this.tabs[next].dataset.lookTab);
          this.tabs[next].focus();
        }, { signal });
      });
      this.panels.forEach(panel => {
        const rail = panel.querySelector('[data-product-rail]');
        if (!rail) return;
        const move = direction => {
          this.pause();
          rail.scrollBy({ left: direction * rail.clientWidth * .9, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
        };
        panel.querySelector('[data-rail-prev]').addEventListener('click', () => move(-1), { signal });
        panel.querySelector('[data-rail-next]').addEventListener('click', () => move(1), { signal });
        rail.addEventListener('scroll', () => this.updateRail(panel), { passive: true, signal });
      });
      this.resize?.disconnect();
      this.resize = new ResizeObserver(() => this.panels.forEach(panel => this.updateRail(panel)));
      this.resize.observe(this);
      document.addEventListener('shopify:block:select', event => {
        if (this.panels.some(panel => panel.dataset.lookPanel === event.detail.blockId)) this.select(event.detail.blockId);
      }, { signal });
      this.panels.forEach(panel => this.updateRail(panel));
      this.schedule();
    }

    select(id, manual = true) {
      if (!this.panels.some(panel => panel.dataset.lookPanel === id)) return;
      if (manual) this.pause();
      this.tabs.forEach(tab => {
        const active = tab.dataset.lookTab === id;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => {
        panel.hidden = panel.dataset.lookPanel !== id;
        if (!panel.hidden) {
          if (!this.reducedMotion.matches) panel.animate([{ opacity: .72 }, { opacity: 1 }], { duration: 450, easing: 'ease-out' });
          requestAnimationFrame(() => this.updateRail(panel));
        }
      });
      this.schedule();
    }

    step(direction, manual = true) {
      const current = this.panels.findIndex(panel => !panel.hidden);
      const next = (current + direction + this.panels.length) % this.panels.length;
      this.select(this.panels[next].dataset.lookPanel, manual);
    }

    pause() {
      this.paused = true;
      clearTimeout(this.timer);
    }

    canRotate() {
      if (!this.autoplayEnabled || this.paused || document.hidden || this.reducedMotion.matches || window.Shopify?.designMode) return false;
      const panel = this.panels.find(item => !item.hidden);
      if (!panel) return false;
      const hero = panel.querySelector('.ae-hero').getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(hero.bottom, innerHeight) - Math.max(hero.top, 0));
      const products = panel.querySelector('.ae-table-shop')?.getBoundingClientRect();
      return visibleHeight / hero.height > .35 && (!products || products.top >= innerHeight - 60);
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.canRotate()) return;
      this.timer = setTimeout(() => {
        if (this.canRotate()) this.step(1, false);
      }, Math.max(5000, Number(this.dataset.interval) || 9000));
    }

    updateRail(panel) {
      if (panel.hidden) return;
      const rail = panel.querySelector('[data-product-rail]');
      if (!rail) return;
      const maxScroll = rail.scrollWidth - rail.clientWidth;
      panel.querySelector('[data-rail-controls]').hidden = maxScroll < 2;
      panel.querySelector('[data-rail-prev]').disabled = rail.scrollLeft < 2;
      panel.querySelector('[data-rail-next]').disabled = rail.scrollLeft >= maxScroll - 2;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      clearTimeout(this.timer);
    }
  });
}
